import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { useUser } from "@clerk/clerk-react";
import { courses } from "./App";

const C = {
  bg: '#020617', surface: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.10)',
  accent: '#60A5FA', accentGlow: 'rgba(96,165,250,0.15)',
  accentGlow2: 'rgba(96,165,250,0.08)',
  gold: '#F0A500', green: '#34D399', red: '#F87171',
  text: '#FFFFFF', textMuted: '#94A3B8',
  textFaint: '#475569',
};
const F = { display: "'Playfair Display', Georgia, serif", body: "'DM Sans', sans-serif" };

export function MyLearningPage({ setPage, setSelectedCourse }) {
  const { user } = useUser();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    supabase
      .from("enrollments")
      .select("*")
      .eq("user_id", user.id)
      .order("enrolled_at", { ascending: false })
      .then(({ data }) => {
        setEnrollments(data || []);
        setLoading(false);
      });
  }, [user?.id]);

  const totalCompleted = enrollments.filter(e => e.completed).length;
  const inProgress = enrollments.filter(e => !e.completed && e.progress > 0).length;

  return (
    <div className="fade-in page-container" style={{ maxWidth: 1000, margin: "0 auto", padding: "32px 24px 80px", fontFamily: F.body }}>
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 8 }}>Dashboard</div>
        <h1 style={{ fontFamily: F.display, fontSize: 32, fontWeight: 900, marginBottom: 6 }}>My Learning</h1>
        <p style={{ color: C.textMuted, fontSize: 13 }}>Track your progress and continue learning</p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 32 }}>
        {[
          { n: enrollments.length, l: "Enrolled", icon: "📚", color: C.accent },
          { n: inProgress, l: "In Progress", icon: "▶️", color: C.gold },
          { n: totalCompleted, l: "Completed", icon: "✅", color: C.green },
        ].map(s => (
          <div key={s.l} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "18px 20px" }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: s.color, fontFamily: F.display }}>{s.n}</div>
            <div style={{ fontSize: 12, color: C.textMuted }}>{s.l}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ color: C.textMuted, padding: "40px 0" }}>Loading your courses...</div>
      ) : enrollments.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📚</div>
          <h3 style={{ fontFamily: F.display, fontSize: 22, fontWeight: 800, marginBottom: 8 }}>No courses yet</h3>
          <p style={{ color: C.textMuted, fontSize: 14, marginBottom: 24 }}>Start learning by enrolling in a course</p>
          <button className="btn-primary" style={{ padding: "12px 28px" }} onClick={() => setPage("courses")}>
            Browse Courses
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {enrollments.map(enrollment => (
            <div key={enrollment.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "18px 22px", display: "flex", gap: 16, alignItems: "center" }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: C.accentGlow, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>📘</div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{enrollment.course_title}</h3>
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: C.textMuted }}>
                    Enrolled {new Date(enrollment.enrolled_at).toLocaleDateString()}
                  </span>
                  {enrollment.completed && (
                    <span style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>✅ Completed</span>
                  )}
                  {!enrollment.completed && enrollment.progress > 0 && (
                    <span style={{ fontSize: 11, color: C.gold }}>{enrollment.progress}% complete</span>
                  )}
                  {enrollment.progress === 0 && !enrollment.completed && (
                    <span style={{ fontSize: 11, color: C.textFaint }}>Not started</span>
                  )}
                </div>
                {/* Progress bar */}
                <div style={{ height: 4, background: "rgba(0,168,232,0.1)", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${enrollment.completed ? 100 : enrollment.progress}%`, background: enrollment.completed ? C.green : `linear-gradient(90deg,${C.accent},${C.green})`, borderRadius: 2, transition: "width 0.5s ease" }} />
                </div>
              </div>
              <button
                type="button"
                className="btn-primary"
                style={{ padding: "9px 18px", fontSize: 13, flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const courseId = Number(enrollment.course_id);
                  const match = courses.find(c => Number(c.id) === courseId);
                  if (match && setSelectedCourse) {
                    setSelectedCourse(match);
                  } else {
                    setPage("courses");
                  }
                }}>
                {enrollment.progress > 0 ? "Continue" : "Start"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}